import { Save, X } from "lucide-react";
import { useState } from "react";

interface OfferFormData {
  status: string;
  baseSalary: string;
  currency: string;
  bonusDetails: string;
  equityDetails: string;
  startDate: string;
  notes: string;
}

interface OfferFormProps {
  initial?: {
    status: string;
    baseSalary: number | null;
    currency: string | null;
    bonusDetails: string | null;
    equityDetails: string | null;
    startDate: string | null;
    notes: string | null;
  };
  onSave: (data: OfferFormData) => void;
  onCancel: () => void;
  saving: boolean;
  isEdit?: boolean;
}

const STATUSES = ["draft", "extended", "negotiating", "accepted", "declined", "withdrawn"];
const CURRENCIES = ["GBP", "USD", "EUR", "CHF", "SGD", "AUD", "CAD"];

export function OfferForm({ initial, onSave, onCancel, saving, isEdit }: OfferFormProps) {
  const [status, setStatus] = useState(initial?.status ?? "draft");
  const [baseSalary, setBaseSalary] = useState(
    initial?.baseSalary != null ? String(initial.baseSalary) : "",
  );
  const [currency, setCurrency] = useState(initial?.currency ?? "GBP");
  const [bonusDetails, setBonusDetails] = useState(initial?.bonusDetails ?? "");
  const [equityDetails, setEquityDetails] = useState(initial?.equityDetails ?? "");
  const [startDate, setStartDate] = useState(initial?.startDate ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ status, baseSalary, currency, bonusDetails, equityDetails, startDate, notes });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded border border-border bg-card-alt p-3"
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="block text-xs font-medium text-text-secondary">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Base Salary</label>
          <input
            type="number"
            value={baseSalary}
            onChange={(e) => setBaseSalary(e.target.value)}
            placeholder="e.g. 150000"
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary">Bonus Details</label>
          <input
            type="text"
            value={bonusDetails}
            onChange={(e) => setBonusDetails(e.target.value)}
            placeholder="e.g. 20% annual bonus"
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Equity Details</label>
          <input
            type="text"
            value={equityDetails}
            onChange={(e) => setEquityDetails(e.target.value)}
            placeholder="e.g. 0.5% vesting over 4 years"
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Additional notes..."
          className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          <X size={12} /> Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          <Save size={12} /> {saving ? "Saving..." : isEdit ? "Update Offer" : "Create Offer"}
        </button>
      </div>
    </form>
  );
}
